import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';

// Constants
import { T, PILL_SHADOW } from '../constants/tokens';

/**
 * Static styling lives here rather than inline so the card can express hover
 * and focus, which an inline style object cannot. Anything that varies per
 * card — the status accent, the selected border — stays inline, because a
 * class per colour would be worse than no class at all.
 */
const useStyles = makeStyles({
  card: {
    display: 'flex',
    alignItems: 'stretch',
    gap: 12,
    background: T.surfaceCard,
    borderRadius: 6,
    padding: 12,
    marginBottom: 8,
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'background 120ms ease, border-color 120ms ease',
    '&:hover': {
      background: T.surfaceSoft
    },
    '&:focus-visible': {
      outline: '2px solid ' + T.focus,
      outlineOffset: 2
    }
  },
  accent: {
    width: 4,
    flex: 'none',
    borderRadius: 9999
  },
  body: {
    flex: 1,
    minWidth: 0
  }
});

/**
 * The card every list in the app is built from.
 *
 * v7 drops the pair of status bars that used to flank the content in favour of
 * a single left edge bar: one accent per card reads faster in a long column,
 * and mirroring it added weight without adding information.
 */
export default function ListCard({
  style = {},
  statusColor,
  onClick = () => {},
  children,
  isSelected,
  selectedColor = null
}) {
  const classes = useStyles();
  const accent = statusColor || T.hairline;

  return (
    <div
      role='button'
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick(event);
        }
      }}
      className={classes.card}
      style={{
        // Selection reads as a darker border rather than the old 5px ring,
        // which used to shift every other card when one was picked.
        border: '1px solid ' + (isSelected ? (selectedColor || T.ink) : T.hairline),
        boxShadow: isSelected ? PILL_SHADOW : 'none',
        ...style
      }}
    >
      <div className={classes.accent} style={{ background: accent }} />
      <div className={classes.body}>
        {children}
      </div>
    </div>
  );
}

ListCard.propTypes = {
  style: PropTypes.object,
  statusColor: PropTypes.string,
  onClick: PropTypes.func,
  children: PropTypes.any,
  isSelected: PropTypes.bool,
  selectedColor: PropTypes.string
};
